import {setAutoFreeze} from 'immer';
import {useEffect} from 'react';

import {RampIdType} from '../generated/graphql';
import {useUserRampFlag} from './useRampFlag';

/**
 * Toggles Immer's global auto-freeze based on the per-user `immer-auto-freeze-enabled`
 * Statsig gate.
 *
 * Auto-freeze is globally off by default (set at module load in
 * `state/runs-low/reducer.ts`) because it adds a per-produce deep-walk cost. This hook
 * opts targeted users into freeze-on so we can validate the runs-low mutation-hygiene
 * fixes in production: with auto-freeze on, any reducer that mutates already-emitted
 * state throws instead of silently corrupting it.
 *
 * `setAutoFreeze` is a process-wide Immer setting, so mount this exactly once, high in
 * the always-mounted tree (`AppWithRoutes`). The flag resolves to `false` while the
 * viewer query is in flight, so freeze stays off until a targeted user is confirmed.
 */
export function useImmerAutoFreeze(): void {
  const enabled =
    useUserRampFlag('immer-auto-freeze-enabled', RampIdType.UserId) ?? false;
  useEffect(() => {
    setAutoFreeze(enabled);
  }, [enabled]);
}
